import { forwardRef, useEffect, useId, type InputHTMLAttributes, type LabelHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { Checkbox as RxCheckbox, Slider as RxSlider, Switch as RxSwitch, Label as RxLabel } from "radix-ui";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "../lib/cn";
import { useDocumentLocale } from "../lib/microcopy";

/**
 * The UAE Design System 3.0 form control (`aegov-form-control`): white, 8px corners, a 2px AE Gold 600 edge that turns Camel 600
 * (the focus colour) while the field has focus, AE Gold 900 text and AE Black 500 placeholders. An invalid field takes the error
 * style: an AE Red 600 edge on an AE Red 50 fill. Disabled fields fade to 40%.
 */
export const fieldBase =
  "flex w-full rounded-lg border-2 border-aegold-600 bg-white px-3.5 py-1 text-sm text-aegold-900 transition-[border-color,background-color] duration-150 placeholder:text-aeblack-500 focus:border-ring focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-40 aria-invalid:border-aered-600 aria-invalid:bg-aered-50 aria-invalid:text-aered-900";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ className, type, ...props }, ref) => (
  <input ref={ref} type={type} className={cn(fieldBase, "h-10 file:border-0 file:bg-transparent file:text-sm file:font-medium", className)} {...props} />
));
Input.displayName = "Input";

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...props }, ref) => (
  <textarea ref={ref} className={cn(fieldBase, "min-h-24 resize-y py-2 leading-relaxed", className)} {...props} />
));
Textarea.displayName = "Textarea";

export const NativeSelect = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(({ className, children, ...props }, ref) => (
  <div className="relative min-w-0 max-w-full">
    <select ref={ref} className={cn(fieldBase, "h-10 max-w-full appearance-none pe-9", className)} {...props}>
      {children}
    </select>
    <ChevronDown className="pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2 text-aegold-700" />
  </div>
));
NativeSelect.displayName = "NativeSelect";

export const Label = forwardRef<HTMLLabelElement, LabelHTMLAttributes<HTMLLabelElement> & { required?: boolean }>(
  ({ className, required, children, ...props }, ref) => (
    <RxLabel.Root ref={ref} className={cn("text-sm font-medium leading-snug text-aeblack-800 peer-disabled:cursor-not-allowed peer-disabled:opacity-70", className)} {...props}>
      {children}
      {required ? <span className="ms-1 text-danger-600" aria-hidden>*</span> : null}
    </RxLabel.Root>
  ),
);
Label.displayName = "Label";

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
  children: ReactNode | ((id: string) => ReactNode);
  /** Right-aligned action or copilot hook (e.g. "Explain this field"). */
  action?: ReactNode;
}

export function Field({ label, hint, error, required, className, children, action }: FieldProps) {
  const id = useId();
  const noteId = `${id}-note`;
  const note = error || hint;
  useEffect(() => {
    const control = document.getElementById(id);
    if (!control) return;
    if (note) control.setAttribute("aria-describedby", noteId);
    else if (control.getAttribute("aria-describedby") === noteId) control.removeAttribute("aria-describedby");
    if (error) control.setAttribute("aria-invalid", "true");
    else control.removeAttribute("aria-invalid");
  }, [id, noteId, note, error]);
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label || action ? (
        <div className="flex items-center justify-between gap-2">
          {label ? <Label htmlFor={id} required={required}>{label}</Label> : <span />}
          {action}
        </div>
      ) : null}
      {typeof children === "function" ? children(id) : children}
      {error ? (
        <p id={noteId} role="alert" className="text-sm text-aered-700 animate-fade-in">{error}</p>
      ) : hint ? (
        <p id={noteId} className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

export const Checkbox = forwardRef<HTMLButtonElement, React.ComponentPropsWithoutRef<typeof RxCheckbox.Root>>(({ className, ...props }, ref) => (
  <RxCheckbox.Root
    ref={ref}
    className={cn(
      "peer size-5 shrink-0 rounded-sm border border-aegold-600 bg-white transition-colors hover:bg-aegold-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:border-aegold-200 disabled:opacity-40 data-[state=checked]:bg-aegold-600 data-[state=checked]:text-white aria-invalid:border-aered-600",
      className,
    )}
    {...props}
  >
    <RxCheckbox.Indicator className="grid place-items-center">
      <Check className="size-3.5" strokeWidth={3} />
    </RxCheckbox.Indicator>
  </RxCheckbox.Root>
));
Checkbox.displayName = "Checkbox";

export interface SliderProps extends Omit<React.ComponentPropsWithoutRef<typeof RxSlider.Root>, "dir"> {
  /** Defaults to the document language (Arabic pages slide from the right). */
  dir?: "ltr" | "rtl";
  tone?: "gold" | "sea" | "neutral";
}

const sliderRange = { gold: "bg-aegold-600", sea: "bg-seablue-500", neutral: "bg-sand-500" } as const;
const sliderThumb = { gold: "border-aegold-600", sea: "border-seablue-500", neutral: "border-sand-500" } as const;

/** Radix slider in the Sand palette. Renders one thumb per value; pair it with a numeric input for exact entry. */
export const Slider = forwardRef<HTMLSpanElement, SliderProps>(({ className, dir, tone = "gold", value, defaultValue, ...props }, ref) => {
  const locale = useDocumentLocale();
  const thumbs = value ?? defaultValue ?? [0];
  return (
    <RxSlider.Root
      ref={ref}
      dir={dir ?? (locale === "ar" ? "rtl" : "ltr")}
      value={value}
      defaultValue={defaultValue}
      className={cn("relative flex w-full touch-none select-none items-center py-2 data-[disabled]:opacity-50", className)}
      {...props}
    >
      <RxSlider.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-aeblack-100 shadow-[inset_0_1px_1px_rgb(35_37_40/0.08)]">
        <RxSlider.Range className={cn("absolute h-full rounded-full transition-colors", sliderRange[tone])} />
      </RxSlider.Track>
      {thumbs.map((_, i) => (
        <RxSlider.Thumb
          key={i}
          className={cn(
            "block size-4 rounded-full border-2 bg-white shadow-[0_1px_3px_rgb(35_37_40/0.2),0_4px_10px_-4px_rgb(35_37_40/0.25)] transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
            sliderThumb[tone],
          )}
        />
      ))}
    </RxSlider.Root>
  );
});
Slider.displayName = "Slider";

/**
 * The Design System toggle (`aegov-toggle`): a slim AE Black 100 track that turns AE Gold 300 when on, and a white thumb edged in AE
 * Gold 600 that carries a tick or a cross, so the state never rests on colour alone.
 */
export const Switch = forwardRef<HTMLButtonElement, React.ComponentPropsWithoutRef<typeof RxSwitch.Root>>(({ className, ...props }, ref) => (
  <RxSwitch.Root
    ref={ref}
    className={cn(
      "peer group/switch relative inline-flex h-5 w-10 shrink-0 cursor-pointer items-center rounded-full outline-none focus-visible:outline-1 focus-visible:outline-offset-3 focus-visible:outline-aegold-500 disabled:cursor-not-allowed disabled:opacity-40",
      className,
    )}
    {...props}
  >
    <span aria-hidden className="pointer-events-none absolute h-4 w-10 rounded-full bg-aeblack-100 transition-colors group-data-[state=checked]/switch:bg-aegold-300" />
    <RxSwitch.Thumb className="pointer-events-none relative grid size-5 place-items-center rounded-full border border-aeblack-100 bg-white text-aeblack-300 shadow-[0_1px_3px_rgb(0_0_0/0.1),0_1px_2px_-1px_rgb(0_0_0/0.1)] transition-[translate,border-color,color] duration-200 data-[state=checked]:translate-x-5 data-[state=checked]:border-aegold-600 data-[state=checked]:text-aegold-600 rtl:data-[state=checked]:-translate-x-5 data-[state=unchecked]:translate-x-0">
      <Check className="hidden size-2.5 group-data-[state=checked]/switch:block" strokeWidth={3.5} />
      <X className="size-2.5 group-data-[state=checked]/switch:hidden" strokeWidth={3.5} />
    </RxSwitch.Thumb>
  </RxSwitch.Root>
));
Switch.displayName = "Switch";
